import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Context,
  type PropsWithChildren,
  type RefObject,
} from 'react';
import { useIsomorphicLayoutEffect, useWindowSize } from 'react-use';

type GroupRefMapType = Map<string, Map<string, RefObject<HTMLDivElement>>>;

type EqualHeightContext = {
  groupRefMap: GroupRefMapType;
  rerender: () => void;
  registerRef: (
    ref: RefObject<HTMLDivElement>,
    componentId: string,
    groupId: string
  ) => void;
};

// @ts-ignore
const EqualHeightContextImpl: Context<EqualHeightContext> = createContext({});

export const EqualHeightContextProvider = ({ children }: PropsWithChildren) => {
  const [groupRefMap, setGroupRefMap] = useState<GroupRefMapType>(
    new Map<string, Map<string, RefObject<HTMLDivElement>>>()
  );
  const windowSize = useWindowSize();

  const registerRef = useCallback(
    (ref: RefObject<HTMLDivElement>, componentId: string, groupId: string) => {
      setGroupRefMap((groupRefMap) => {
        const _groupRefMap = new Map(groupRefMap);

        if (_groupRefMap) {
          if (!_groupRefMap.has(groupId)) {
            _groupRefMap.set(groupId, new Map());
          }

          if (_groupRefMap.get(groupId)?.get(componentId) !== ref) {
            _groupRefMap.get(groupId)?.set(componentId, ref);

            // before updating groupRefMap for the respective group id, remove the stale entries
            Array.from(_groupRefMap.get(groupId)?.keys() ?? []).forEach(
              (componentId: string) => {
                if (!_groupRefMap.get(groupId)?.get(componentId)?.current) {
                  _groupRefMap.get(groupId)?.delete(componentId);
                }
              }
            );
          }
        }

        return _groupRefMap;
      });
    },
    []
  );

  const rerender = useCallback(() => {
    groupRefMap.forEach((groupIdMap) => {
      const groupIdMapRefs = Array.from(groupIdMap.values() || []).map(
        (ref: RefObject<HTMLDivElement>) => ref
      );
      const maxHeight = Math.max(
        ...groupIdMapRefs.map((r) => r.current?.children[0]?.clientHeight ?? 0)
      );
      groupIdMapRefs.forEach((ref) => {
        if (ref.current && maxHeight >= 0) {
          ref.current.style.height = `${maxHeight}px`;
        }
      });
    });
  }, [groupRefMap]);

  useIsomorphicLayoutEffect(() => {
    rerender();
  }, [groupRefMap, windowSize, rerender]);

  const providerInputs = useMemo(
    () => ({ groupRefMap, registerRef, rerender }),
    [groupRefMap, registerRef, rerender]
  );

  return (
    <EqualHeightContextImpl.Provider value={providerInputs}>
      {children}
    </EqualHeightContextImpl.Provider>
  );
};

export const useEqualHeightContext = () => {
  return useContext(EqualHeightContextImpl);
};
